<template>
  <WModal
    :model-value="state.show"
    :head-line="state.headLine || 'Confirm'"
    :icon="state.icon"
    :body-text="state.bodyText"
    :confirm-text="state.confirmText || 'Confirm'"
    :confirm-style="confirmButtonStyle"
    :cancel-text="state.cancelText || 'Cancel'"
    :confirm-loading="state.confirmLoading"
    :section-tag="state.sectionTag || 'confirm-modal'"
    @update:model-value="onModelValueUpdate"
    @confirm="handleConfirm"
    @cancel="handleCancel"
  />
</template>

<script setup lang="ts">
/**
 * Global confirmation modal component.
 * Renders confirmation dialogs triggered via useConfirm() composable.
 *
 * @example
 * // In Layout.vue - place once at app root
 * <ConfirmModal />
 *
 * @example
 * // Triggering from any component
 * const { confirmDialog } = useConfirm()
 * confirmDialog('Are you sure?', () => doSomething())
 */
import { computed } from 'vue'
import { useConfirm } from '@/composables/useConfirm'

import WModal from '@wasabi/content-displays/WModal.vue'

// ============ COMPOSABLES ============
const { state, handleConfirm, handleCancel, handleClose } = useConfirm()

// ============ COMPUTED ============
/**
 * Style object for confirm button based on confirmColor
 * WModal uses confirmStyle prop, not confirmColor
 */
const confirmButtonStyle = computed(() => {
  if (state.value.confirmColor === 'error') {
    return {
      backgroundColor: '#D32F2F',
      color: '#FFFFFF'
    }
  }
  // Default primary color
  return {}
})

/**
 * The close button, Escape and a click outside all end by writing the model to false. Without this
 * the caller's confirmation never answers and whatever it locked stays locked.
 * Confirm and Cancel hide the dialog first, so their trailing write finds it already closed.
 */
function onModelValueUpdate(open: boolean) {
  if (!open && state.value.show) handleClose()
}
</script>
